import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { SiteSearchResult } from "~/types/search";
import type { BlokProductRecommendations } from "~/types/storyblok/storyblok";

/** What the recommendation engine is asked to recommend *for*. */
export interface RecommendationContext {
	productNumbers?: string[];
	urls?: string[];
	hierarchies?: string[][];
}

/**
 * The Meilisearch fallback's inputs. Omit the whole object on a surface with
 * no single product to anchor on — the cart — and no fallback request is made
 * at all, rather than one that filters on empty strings.
 */
export interface RecommendationFallback {
	partNo?: string | null;
	brand?: string | null;
	category?: string | null;
	strategy?: string | null;
}

export interface HelloRetailResponse {
	success?: boolean;
	products?: Record<string, unknown>[];
}

export const DEFAULT_RECOMMENDATION_LIMIT = 6;

/** The card reads the brand from `Manufacturer.Name`; omitted when unknown. */
const toManufacturer = (brand: unknown) =>
	typeof brand === "string" && brand ? { Manufacturer: { Name: brand } } : {};

/**
 * Storyblok returns a Number field as a string ("6"), and its editor accepts a
 * negative or fractional one, so coerce and sanity-check rather than trust the
 * declared type — the value reaches the Meilisearch query as `limit`.
 */
export const recommendationLimit = (value: unknown): number => {
	const limit = Math.floor(Number(value));

	return Number.isFinite(limit) && limit > 0
		? limit
		: DEFAULT_RECOMMENDATION_LIMIT;
};

/**
 * Cache key for one surface's request. `scope` separates surfaces that would
 * otherwise collide — the PDP block and the mini cart on the same page.
 */
export const recommendationKey = ({
	scope,
	iso,
	productBoxKey,
	context,
	limit
}: {
	scope: string;
	/** `localeProperties.iso` is loosely typed upstream; only stability matters. */
	iso: unknown;
	productBoxKey?: string;
	context: RecommendationContext;
	limit: number;
}): string =>
	[
		scope,
		String(iso ?? ""),
		productBoxKey || "default",
		context.productNumbers?.join(",") || "none",
		context.hierarchies?.flat().join(">") || "none",
		limit
	].join(":");

/** Hello Retail's payload is a loose record; only the card's fields are read. */
export const toHelloRetailProductItem = (
	product: Record<string, unknown>,
	priceListId?: number
) =>
	({
		trackingCode: product.trackingCode,
		inStock: product.inStock,
		UniqueName: (product.extraData as Record<string, unknown>)?.uniqueName,
		Files: [{ Path: product.imgUrl }],
		Name: product.title,
		PriceIncVat: product.price,
		OldPrice: product.oldPrice,
		PartNo: product.productNumber,
		PriceListId: priceListId,
		...toManufacturer(product.brand)
	}) as unknown as ProductItem;

export const toMeiliProductItem = (result: SiteSearchResult) =>
	({
		UniqueName: result.slug || "",
		Name: result.title || "",
		Files: result.image ? [{ Path: result.image }] : [],
		PriceIncVat: result.price ?? 0,
		PartNo: result.partNo || "",
		...toManufacturer(result.brand)
	}) as ProductItem;

/**
 * Hello Retail wins when it is the engine and answered; a failed or empty
 * payload falls through to Meilisearch.
 */
export const selectRecommendedProducts = ({
	searchEngine,
	helloRetail,
	meilisearch,
	priceListId
}: {
	searchEngine?: string;
	helloRetail?: HelloRetailResponse | null;
	meilisearch?: { products?: SiteSearchResult[] } | null;
	priceListId?: number;
}): ProductItem[] => {
	if (
		searchEngine === "helloretail" &&
		helloRetail?.success &&
		helloRetail.products?.length
	) {
		return helloRetail.products.map((product) =>
			toHelloRetailProductItem(product, priceListId)
		);
	}

	if (meilisearch?.products?.length) {
		return meilisearch.products.map(toMeiliProductItem);
	}

	return [];
};

/** The product a recommendation block anchors its Meilisearch query on. */
export interface FallbackProduct {
	PartNo?: string | null;
	Manufacturer?: { Name?: string | null } | null;
	CategoryName?: string | null;
}

/**
 * A block's Meilisearch query: the fallback on a Hello Retail deployment,
 * where the editor can turn it off, and the only source on a Meilisearch one.
 */
export const blockMeiliQuery = (
	blok: Pick<
		BlokProductRecommendations,
		"recommendation_strategy" | "disable_meilisearch_fallback"
	>,
	product: FallbackProduct | null | undefined,
	searchEngine: string
): RecommendationFallback | undefined => {
	if (searchEngine === "helloretail" && blok.disable_meilisearch_fallback) {
		return undefined;
	}
	return {
		partNo: product?.PartNo,
		brand: product?.Manufacturer?.Name,
		category: product?.CategoryName,
		strategy: blok.recommendation_strategy
	};
};
